import { useQuery } from '@tanstack/solid-query';
import { createMemo } from 'solid-js';
import {
  getCurrentPortalCustomizationConfig,
  getCustomizationSettingsForEnvironment,
  getHostedAuthCustomization,
  retryServerErrors
} from '~/api';
import {
  getHostedAuthPageCustomizationQueryKey,
  getPortalCustomizationConfigForEnvironmentQuery,
  getPortalCustomizationConfigQuery
} from '~/api/tanstackUtils';
import { useAppData } from '~/components/AppData/AppData';
import { globalStore } from '~/stores';
import { AppContexts } from '~/utils/commonUtils';
import { CustomizationResponse } from '~/api/types/customisationData';

export function useCustomizationConfig() {
  const appData = useAppData();

  const queryConfig = createMemo(() => {
    const appContext = globalStore.appContext;

    switch (appContext) {
      case AppContexts.HOSTED_AUTH_PAGES:
      case AppContexts.ENV_APP_PAGES:
        return {
          queryKey: getHostedAuthPageCustomizationQueryKey(),
          queryFn: async (): Promise<CustomizationResponse> =>
            await getHostedAuthCustomization(),
          retry: retryServerErrors
        };
      case AppContexts.CUSTOMER_PORTAL:
        return {
          queryKey: getPortalCustomizationConfigQuery(),
          queryFn: async () =>
            (await getCurrentPortalCustomizationConfig()) ?? {},
          retry: retryServerErrors
        };
      case AppContexts.SCALEKIT_DASHBOARD: {
        const envId = appData?.selectedEnvironment?.id ?? '-';
        return {
          queryKey: getPortalCustomizationConfigForEnvironmentQuery(envId),
          queryFn: async () => await getCustomizationSettingsForEnvironment(),
          retry: retryServerErrors
        };
      }
      case AppContexts.HANDLERS:
      default:
        return null;
    }
  });

  const query = useQuery(() => {
    const config = queryConfig();
    if (!config) {
      return {
        queryKey: ['customization-config-noop'],
        queryFn: () => ({}),
        enabled: false
      };
    }
    return config;
  });

  return {
    query,
    hasConfig: () => !!queryConfig()
  };
}
